import { useState } from "react";
import { Button } from "../components/ui/button";
import {
  Archive,
  Search,
  Brain,
  Upload,
  BookOpen,
  MessageSquare,
  FolderOpen,
  Mic,
  Globe,
  FileText,
  Shield,
  Sparkles,
  ChevronRight,
  Menu,
  X,
  Image,
  Music,
  Video,
  ArrowRight,
  Users,
  Heart,
  Briefcase,
  PartyPopper,
} from "lucide-react";

export default function LandingPage({ onGetStarted }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-navy-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-navy-900/90 backdrop-blur-md border-b border-white/10" style={{ paddingTop: 'var(--status-bar-height, 0px)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Archive className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight font-['DM_Sans'] text-white">Archiva</span>
            </div>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollTo("features")} className="text-sm text-blue-200/70 hover:text-white transition-colors">Features</button>
              <button onClick={() => scrollTo("how-it-works")} className="text-sm text-blue-200/70 hover:text-white transition-colors">How It Works</button>
              <button onClick={() => scrollTo("use-cases")} className="text-sm text-blue-200/70 hover:text-white transition-colors">Use Cases</button>
              <Button onClick={onGetStarted} size="sm" className="bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] hover:from-[#2563EB] hover:to-[#3B82F6] border-0 shadow-lg shadow-blue-500/25">
                Sign In
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center gap-3">
              <Button onClick={onGetStarted} size="sm" className="bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] border-0">
                Sign In
              </Button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-blue-200">
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 border-t border-white/10 pt-3 space-y-2">
              <button onClick={() => scrollTo("features")} className="block w-full text-left px-3 py-2 text-sm text-blue-200/70 hover:bg-white/5 rounded-md">Features</button>
              <button onClick={() => scrollTo("how-it-works")} className="block w-full text-left px-3 py-2 text-sm text-blue-200/70 hover:bg-white/5 rounded-md">How It Works</button>
              <button onClick={() => scrollTo("use-cases")} className="block w-full text-left px-3 py-2 text-sm text-blue-200/70 hover:bg-white/5 rounded-md">Use Cases</button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-20 sm:pt-36 sm:pb-28 px-4 relative overflow-hidden">
        {/* Subtle background glow */}
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[300px] md:w-[600px] h-[200px] md:h-[400px] bg-[#1E3A8A]/40 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 text-[#60A5FA] text-sm font-medium mb-6 border border-white/10">
            <Sparkles className="w-4 h-4" />
            AI-Powered Multimedia Archive
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white font-['DM_Sans'] leading-tight mb-6">
            Preserve Your Stories.
            <br />
            <span className="bg-gradient-to-r from-[#60A5FA] to-[#93C5FD] bg-clip-text text-transparent">Relive Your Memories.</span>
          </h1>
          <p className="text-lg sm:text-xl text-blue-200/60 max-w-2xl mx-auto mb-10 leading-relaxed">
            Archiva is an intelligent archive for families, businesses, and communities.
            Upload photos, documents, audio, and video — then let AI help you organize,
            search, and transform them into beautiful stories.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button onClick={onGetStarted} size="lg" className="text-base px-8 py-6 rounded-xl bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] hover:from-[#2563EB] hover:to-[#3B82F6] border-0 shadow-xl shadow-blue-500/30">
              Get Started Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button onClick={() => scrollTo("features")} variant="outline" size="lg" className="text-base px-8 py-6 rounded-xl border-white/20 text-white hover:bg-white/10 bg-transparent">
              See Features
            </Button>
          </div>

          {/* Media type pills */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-12">
            {[
              { icon: Image, label: "Photos", color: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20" },
              { icon: FileText, label: "Documents", color: "text-blue-400 bg-blue-400/10 border-blue-400/20" },
              { icon: Music, label: "Audio", color: "text-purple-400 bg-purple-400/10 border-purple-400/20" },
              { icon: Video, label: "Video", color: "text-orange-400 bg-orange-400/10 border-orange-400/20" },
            ].map(({ icon: Icon, label, color }) => (
              <div key={label} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${color}`}>
                <Icon className="w-4 h-4" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-navy-800 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-['DM_Sans'] mb-4">
              Everything You Need to Preserve What Matters
            </h2>
            <p className="text-blue-200/50 text-lg max-w-2xl mx-auto">
              Powerful features powered by AI, designed for simplicity.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Upload,
                title: "Smart Upload",
                desc: "Drag and drop any file — photos, documents, audio, video. AI automatically tags and organizes everything for you.",
                color: "bg-blue-500/15 text-blue-400",
              },
              {
                icon: Brain,
                title: "AI Auto-Tagging",
                desc: "Every file is analyzed and tagged automatically. No manual sorting needed — Archiva understands your content.",
                color: "bg-purple-500/15 text-purple-400",
              },
              {
                icon: Search,
                title: "Smart Search",
                desc: "Find anything with natural language. Search by meaning, not just keywords — AI understands what you're looking for.",
                color: "bg-emerald-500/15 text-emerald-400",
              },
              {
                icon: MessageSquare,
                title: "AI Archivist",
                desc: "Chat with your archive. Ask questions about your files and get intelligent answers with source citations.",
                color: "bg-orange-500/15 text-orange-400",
              },
              {
                icon: BookOpen,
                title: "Story Writer",
                desc: "Transform your memories into beautiful stories. AI co-writes with you, chapter by chapter, with photos and media.",
                color: "bg-pink-500/15 text-pink-400",
              },
              {
                icon: FolderOpen,
                title: "Projects",
                desc: "Group related files into projects. Chat within project context and export everything as polished PDFs.",
                color: "bg-indigo-500/15 text-indigo-400",
              },
              {
                icon: Mic,
                title: "Voice Input & Audio",
                desc: "Dictate your stories with voice. Convert text to audio narration. Export stories as MP3 audiobooks.",
                color: "bg-red-500/15 text-red-400",
              },
              {
                icon: Globe,
                title: "Translation",
                desc: "Translate your stories into 18+ languages. Share memories across cultures and generations.",
                color: "bg-teal-500/15 text-teal-400",
              },
              {
                icon: Shield,
                title: "Private & Secure",
                desc: "Your memories are yours. Control visibility per file, with secure authentication and encrypted storage.",
                color: "bg-gray-500/15 text-gray-400",
              },
            ].map(({ icon: Icon, title, desc, color }) => (
              <div
                key={title}
                className="group bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-white/20 hover:bg-white/8 transition-all duration-300"
              >
                <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-white font-['DM_Sans'] mb-2">{title}</h3>
                <p className="text-blue-200/50 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-['DM_Sans'] mb-4">
              How It Works
            </h2>
            <p className="text-blue-200/50 text-lg max-w-2xl mx-auto">
              From upload to storytelling in three simple steps.
            </p>
          </div>

          <div className="space-y-8 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-8">
            {[
              {
                step: "1",
                title: "Upload Your Media",
                desc: "Drop photos, documents, audio, or video files. Archiva accepts all common formats up to 100MB each.",
                icon: Upload,
              },
              {
                step: "2",
                title: "AI Organizes Everything",
                desc: "Artificial intelligence automatically tags, indexes, and creates searchable embeddings of your content.",
                icon: Brain,
              },
              {
                step: "3",
                title: "Explore & Create",
                desc: "Search naturally, chat with your archive, build projects, and write stories enriched with your media.",
                icon: Sparkles,
              },
            ].map(({ step, title, desc, icon: Icon }) => (
              <div key={step} className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] text-white flex items-center justify-center mx-auto mb-5 text-2xl font-bold font-['DM_Sans'] shadow-lg shadow-blue-500/25">
                  {step}
                </div>
                <h3 className="text-xl font-semibold text-white font-['DM_Sans'] mb-3">{title}</h3>
                <p className="text-blue-200/50 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="py-20 bg-navy-800 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-['DM_Sans'] mb-4">
              Built For Your Memories
            </h2>
            <p className="text-blue-200/50 text-lg max-w-2xl mx-auto">
              Whether it's family history, business milestones, or community events —
              Archiva keeps them alive.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: "Family Archives",
                desc: "Preserve generations of photos, letters, and recordings. Let every family member contribute their memories and revisit them anytime.",
                color: "bg-pink-500/15 text-pink-400",
              },
              {
                icon: Briefcase,
                title: "Business History",
                desc: "Document your company's journey — milestones, team photos, meeting recordings, and key documents all in one searchable place.",
                color: "bg-blue-500/15 text-blue-400",
              },
              {
                icon: PartyPopper,
                title: "Events & Communities",
                desc: "Weddings, reunions, conferences — collect contributions from every participant and create lasting stories together.",
                color: "bg-amber-500/15 text-amber-400",
              },
            ].map(({ icon: Icon, title, desc, color }) => (
              <div key={title} className="bg-white/5 rounded-2xl p-8 border border-white/10 text-center">
                <div className={`w-14 h-14 rounded-xl ${color} flex items-center justify-center mx-auto mb-5`}>
                  <Icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-semibold text-white font-['DM_Sans'] mb-3">{title}</h3>
                <p className="text-blue-200/50 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Highlight Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-3xl p-8 sm:p-12 text-white text-center border border-white/10 shadow-2xl shadow-blue-500/10">
            <Brain className="w-12 h-12 mx-auto mb-6 opacity-90" />
            <h2 className="text-3xl sm:text-4xl font-bold font-['DM_Sans'] mb-4">
              Your Personal AI Archivist
            </h2>
            <p className="text-blue-100/80 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              Ask questions about your archive in plain English. "Show me photos from last summer."
              "What did grandpa write about the old house?" Archiva's AI understands context,
              finds relevant files, and gives you answers — with sources.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              {["Natural Language Search", "Source Citations", "Voice Chat", "Web Search", "Context-Aware"].map((tag) => (
                <span key={tag} className="px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/10">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Story Writer Highlight */}
      <section className="py-20 bg-navy-800 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <BookOpen className="w-12 h-12 mx-auto mb-6 text-[#60A5FA]" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white font-['DM_Sans'] mb-4">
            Turn Memories Into Stories
          </h2>
          <p className="text-blue-200/50 text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
            Archiva's Story Writer helps you compose beautiful, multi-chapter narratives
            from your archived media. Choose AI co-author mode for creative writing, or
            scribe mode to dictate your story. Add photos, embed documents, and export
            as PDF, Word, or audio.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-lg mx-auto">
            {[
              { label: "AI Co-Writing", icon: Sparkles },
              { label: "Voice Dictation", icon: Mic },
              { label: "PDF Export", icon: FileText },
              { label: "18+ Languages", icon: Globe },
            ].map(({ label, icon: Icon }) => (
              <div key={label} className="flex flex-col items-center gap-2 p-4 bg-white/5 rounded-xl border border-white/10">
                <Icon className="w-6 h-6 text-[#60A5FA]" />
                <span className="text-sm font-medium text-blue-200/70">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-[#1E3A8A]/30 rounded-full blur-[100px] pointer-events-none" />

        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white font-['DM_Sans'] mb-4">
            Start Preserving Your Memories Today
          </h2>
          <p className="text-blue-200/50 text-lg mb-8">
            Create your free account and upload your first files in minutes.
          </p>
          <Button onClick={onGetStarted} size="lg" className="text-base px-8 py-6 rounded-xl bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] hover:from-[#2563EB] hover:to-[#3B82F6] border-0 shadow-xl shadow-blue-500/30">
            Create Free Account
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] flex items-center justify-center">
              <Archive className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-semibold font-['DM_Sans'] text-white">Archiva</span>
          </div>
          <p className="text-sm text-blue-200/30">
            Your intelligent multimedia archive.
          </p>
        </div>
      </footer>
    </div>
  );
}
